import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Search, Percent, Layers } from 'lucide-react';

const TaxSettings = () => {
  const [taxes, setTaxes] = useState([]);
  const [taxGroups, setTaxGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [taxDialogOpen, setTaxDialogOpen] = useState(false);
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [selectedTax, setSelectedTax] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [taxFormData, setTaxFormData] = useState({ name: '', percentage: '' });
  const [groupFormData, setGroupFormData] = useState({ name: '', tax_ids: [] });

  const fetchTaxes = useCallback(async () => {
    try {
      const response = await api.get('/taxes/');
      setTaxes(response.data);
    } catch (error) {
      toast.error('Failed to fetch taxes');
    }
  }, []);

  const fetchTaxGroups = useCallback(async () => {
    try {
      const response = await api.get('/taxes/groups');
      setTaxGroups(response.data);
    } catch (error) {
      toast.error('Failed to fetch tax groups');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTaxes();
    fetchTaxGroups();
  }, [fetchTaxes, fetchTaxGroups]);

  // Tax handlers
  const handleOpenTaxDialog = (tax = null) => {
    if (tax) {
      setSelectedTax(tax);
      setTaxFormData({ name: tax.name, percentage: tax.percentage.toString() });
    } else {
      setSelectedTax(null);
      setTaxFormData({ name: '', percentage: '' });
    }
    setTaxDialogOpen(true);
  };

  const handleSubmitTax = async (e) => {
    e.preventDefault();
    if (!taxFormData.name.trim() || !taxFormData.percentage) {
      toast.error('Tax name and percentage are required');
      return;
    }

    try {
      const params = `name=${encodeURIComponent(taxFormData.name)}&percentage=${taxFormData.percentage}`;
      if (selectedTax) {
        await api.put(`/taxes/${selectedTax.id}?${params}`);
        toast.success('Tax updated successfully');
      } else {
        await api.post(`/taxes/?${params}`);
        toast.success('Tax created successfully');
      }
      setTaxDialogOpen(false);
      fetchTaxes();
      fetchTaxGroups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDeleteTax = async (tax) => {
    if (!window.confirm(`Are you sure you want to delete "${tax.name}"?`)) return;

    try {
      await api.delete(`/taxes/${tax.id}`);
      toast.success('Tax deleted successfully');
      fetchTaxes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete tax');
    }
  };

  // Tax Group handlers
  const handleOpenGroupDialog = (group = null) => {
    if (group) {
      setSelectedGroup(group);
      setGroupFormData({ name: group.name, tax_ids: group.tax_ids || [] });
    } else {
      setSelectedGroup(null);
      setGroupFormData({ name: '', tax_ids: [] });
    }
    setGroupDialogOpen(true);
  };

  const handleSubmitGroup = async (e) => {
    e.preventDefault();
    if (!groupFormData.name.trim()) {
      toast.error('Group name is required');
      return;
    }
    if (groupFormData.tax_ids.length === 0) {
      toast.error('Select at least one tax');
      return;
    }

    try {
      const payload = {
        name: groupFormData.name,
        tax_ids: groupFormData.tax_ids
      };
      
      if (selectedGroup) {
        await api.put(`/taxes/groups/${selectedGroup.id}?name=${encodeURIComponent(groupFormData.name)}`, groupFormData.tax_ids);
        toast.success('Tax group updated successfully');
      } else {
        await api.post(`/taxes/groups?name=${encodeURIComponent(groupFormData.name)}`, groupFormData.tax_ids);
        toast.success('Tax group created successfully');
      }
      setGroupDialogOpen(false);
      fetchTaxGroups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDeleteGroup = async (group) => {
    if (!window.confirm(`Are you sure you want to delete "${group.name}"?`)) return;

    try {
      await api.delete(`/taxes/groups/${group.id}`);
      toast.success('Tax group deleted successfully');
      fetchTaxGroups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete tax group');
    }
  };

  const toggleTaxInGroup = (taxId) => {
    const newTaxIds = groupFormData.tax_ids.includes(taxId)
      ? groupFormData.tax_ids.filter(id => id !== taxId)
      : [...groupFormData.tax_ids, taxId];
    setGroupFormData({ ...groupFormData, tax_ids: newTaxIds });
  };

  const filteredTaxes = taxes.filter(tax =>
    tax.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredGroups = taxGroups.filter(group =>
    group.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tax Settings</h1>
          <p className="text-gray-600">Manage taxes and tax groups</p>
        </div>
      </div>

      <Tabs defaultValue="taxes" className="space-y-4">
        <TabsList>
          <TabsTrigger value="taxes" className="flex items-center gap-2">
            <Percent className="h-4 w-4" />
            Individual Taxes
          </TabsTrigger>
          <TabsTrigger value="groups" className="flex items-center gap-2">
            <Layers className="h-4 w-4" />
            Tax Groups
          </TabsTrigger>
        </TabsList>

        {/* Individual Taxes Tab */}
        <TabsContent value="taxes">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Percent className="h-5 w-5" />
                Taxes ({filteredTaxes.length})
              </CardTitle>
              <Button onClick={() => handleOpenTaxDialog()}>
                <Plus className="h-4 w-4 mr-2" />
                Add Tax
              </Button>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <div className="relative max-w-sm">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search taxes..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tax Name</TableHead>
                    <TableHead>Percentage</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTaxes.length > 0 ? (
                    filteredTaxes.map((tax) => (
                      <TableRow key={tax.id}>
                        <TableCell className="font-medium">{tax.name}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{tax.percentage}%</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={tax.status === 'Active' ? 'default' : 'secondary'}>
                            {tax.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={() => handleOpenTaxDialog(tax)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDeleteTax(tax)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-gray-500 py-8">
                        No taxes found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tax Groups Tab */}
        <TabsContent value="groups">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Layers className="h-5 w-5" />
                Tax Groups ({filteredGroups.length})
              </CardTitle>
              <Button onClick={() => handleOpenGroupDialog()}>
                <Plus className="h-4 w-4 mr-2" />
                Add Tax Group
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Group Name</TableHead>
                    <TableHead>Taxes Included</TableHead>
                    <TableHead>Total Percentage</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredGroups.length > 0 ? (
                    filteredGroups.map((group) => (
                      <TableRow key={group.id}>
                        <TableCell className="font-medium">{group.name}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {group.taxes?.map((tax) => (
                              <Badge key={tax.id} variant="outline" className="text-xs">
                                {tax.name} ({tax.percentage}%)
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className="bg-green-100 text-green-800">
                            {group.total_percentage}%
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={group.status === 'Active' ? 'default' : 'secondary'}>
                            {group.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={() => handleOpenGroupDialog(group)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDeleteGroup(group)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-gray-500 py-8">
                        No tax groups found. Create taxes first, then create groups.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Tax Dialog */}
      <Dialog open={taxDialogOpen} onOpenChange={setTaxDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedTax ? 'Edit Tax' : 'Add New Tax'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitTax}>
            <div className="space-y-4">
              <div>
                <Label htmlFor="tax_name">Tax Name *</Label>
                <Input
                  id="tax_name"
                  value={taxFormData.name}
                  onChange={(e) => setTaxFormData({ ...taxFormData, name: e.target.value })}
                  placeholder="e.g., CGST, SGST, IGST"
                />
              </div>
              <div>
                <Label htmlFor="percentage">Tax Percentage *</Label>
                <Input
                  id="percentage"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={taxFormData.percentage}
                  onChange={(e) => setTaxFormData({ ...taxFormData, percentage: e.target.value })}
                  placeholder="e.g., 9"
                />
              </div>
            </div>
            <DialogFooter className="mt-4">
              <Button type="button" variant="outline" onClick={() => setTaxDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit">{selectedTax ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Tax Group Dialog */}
      <Dialog open={groupDialogOpen} onOpenChange={setGroupDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{selectedGroup ? 'Edit Tax Group' : 'Add New Tax Group'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitGroup}>
            <div className="space-y-4">
              <div>
                <Label htmlFor="group_name">Group Name *</Label>
                <Input
                  id="group_name"
                  value={groupFormData.name}
                  onChange={(e) => setGroupFormData({ ...groupFormData, name: e.target.value })}
                  placeholder="e.g., GST 18%, GST 12%"
                />
              </div>
              <div>
                <Label>Select Taxes *</Label>
                <div className="border rounded-md p-3 mt-2 max-h-48 overflow-y-auto space-y-2">
                  {taxes.length > 0 ? (
                    taxes.map((tax) => (
                      <div key={tax.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`tax-${tax.id}`}
                          checked={groupFormData.tax_ids.includes(tax.id)}
                          onCheckedChange={() => toggleTaxInGroup(tax.id)}
                        />
                        <label htmlFor={`tax-${tax.id}`} className="text-sm cursor-pointer flex-1">
                          {tax.name} <span className="text-gray-500">({tax.percentage}%)</span>
                        </label>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500 text-sm">No taxes available. Create taxes first.</p>
                  )}
                </div>
                {groupFormData.tax_ids.length > 0 && (
                  <p className="text-sm text-green-600 mt-2">
                    Total: {taxes.filter(t => groupFormData.tax_ids.includes(t.id)).reduce((sum, t) => sum + t.percentage, 0)}%
                  </p>
                )}
              </div>
            </div>
            <DialogFooter className="mt-4">
              <Button type="button" variant="outline" onClick={() => setGroupDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit">{selectedGroup ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TaxSettings;
